import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Download,
  Eye,
  RefreshCw,
  Search,
  ChevronLeft,
  ChevronRight,
  ArrowLeft,
} from "lucide-react";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { format } from "timeago.js";
import { toast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import Loader from "@/components/loader/Loader";
import { useTransactions } from "@/hooks";
import { ErrorState } from "@/components/ErrorState";
import type { WalletAsset } from "@/interfaces/asset";
import { formatCurrency, formatEvmHumanBalance } from "@/utils";

interface ITransaction {
  id: string;
  type: string;
  amount: string;
  user: string;
  receiver: string;
  status: string;
  time: string;
  method: string;
  tokenSymbol: string;
  tokenName: string;
  txHash?: string;
  walletAsset: WalletAsset;
  isNative: boolean;
}

function getStatusColor(status: string) {
  switch (status?.toLowerCase()) {
    case "completed":
      return "bg-emerald-500/20 text-emerald-500";
    case "pending":
      return "bg-amber-500/20 text-amber-500";
    case "processing":
      return "bg-blue-500/20 text-blue-500";
    case "failed":
      return "bg-red-500/20 text-red-500";
    case "cancelled":
      return "bg-rose-500/20 text-rose-500";
    case "reversed":
      return "bg-purple-500/20 text-purple-500";
    default:
      return "bg-gray-500/20 text-gray-500";
  }
}

function getTypeColor(type: string) {
  switch (type?.toLowerCase()) {
    case "deposit":
      return "border-emerald-500/50 text-emerald-500";
    case "withdrawal":
      return "border-amber-500/50 text-amber-500";
    case "transfer":
      return "border-blue-500/50 text-blue-500";
    case "swap":
      return "border-indigo-500/50 text-indigo-500";
    case "mint":
      return "border-teal-500/50 text-teal-500";
    default:
      return "border-gray-400/50 text-gray-400";
  }
}

const AdminTransactions = () => {
  const navigate = useNavigate();

  const [isRefreshing, setIsRefreshing] = useState(false);

  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("ALL");

  const [page, setPage] = useState(1);
  const limit = 10;

  const {
    data: transactions,
    isLoading,
    isError,
    refetch,
  } = useTransactions({ page, limit });

  console.log("TRANSACTIONS", transactions);

  const meta = transactions?.meta;

  const structuredTransactions: ITransaction[] =
    transactions?.data?.map((tx: any) => ({
      id: tx.id,
      type: tx.type || tx.action,
      amount: tx.amount,
      user: `${tx.user?.firstName || ""} ${tx.user?.lastName || ""}`.trim(),
      receiver:
        `${tx.receiver?.firstName || ""} ${tx.receiver?.lastName || ""}`.trim(),
      status: tx.status?.toUpperCase(),
      time: tx.updatedAt,
      method: tx.source,
      tokenSymbol: tx.asset.tokenSymbol,
      tokenName: tx?.asset?.currency,
      txHash: tx.txHash,
      isNative: tx.asset?.token?.isNative,

      walletAsset: tx.wallet,
    })) ?? [];

  const filteredTransactions = useMemo(() => {
    return structuredTransactions.filter((tx) => {
      const matchesSearch =
        tx.id?.toLowerCase().includes(search.toLowerCase()) ||
        tx.user?.toLowerCase().includes(search.toLowerCase()) ||
        tx.tokenSymbol?.toLowerCase().includes(search.toLowerCase()) ||
        tx?.walletAsset?.token.name
          ?.toLowerCase()
          .includes(search.toLowerCase()) ||
        (tx.txHash ?? "").toLowerCase().includes(search.toLowerCase());

      const matchesType =
        typeFilter === "all"
          ? true
          : tx.type?.toLowerCase() === typeFilter?.toLowerCase();

      const matchesStatus =
        statusFilter === "ALL" ? true : tx.status === statusFilter;

      return matchesSearch && matchesType && matchesStatus;
    });
  }, [structuredTransactions, search, typeFilter, statusFilter]);

  const handleExport = () => {
    const headers = ["ID", "User", "Type", "Amount", "Status", "Token", "Hash"];

    const rows = filteredTransactions.map((tx) => [
      tx.id,
      tx.user,
      tx.type,
      tx.amount,
      tx.status,
      tx.tokenSymbol,
      tx?.walletAsset?.token.name,
      tx.txHash ?? "",
    ]);

    const csv = [headers, ...rows].map((r) => r.join(",")).join("\n");

    const blob = new Blob([csv], {
      type: "text/csv",
    });

    const url = window.URL.createObjectURL(blob);

    const a = document.createElement("a");

    a.href = url;
    a.download = `transactions-${new Date().toISOString()}.csv`;

    a.click();
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);

    await refetch();

    setIsRefreshing(false);

    toast({
      title: "Refreshed",
      description: "Transactions updated successfully.",
    });
  };

  if (isError)
    return (
      <ErrorState
        onRetry={() => {
          void refetch();
        }}
      />
    );

  if (isLoading) return <Loader type="bounce" />;
  return (
    <div className="min-h-screen bg-background lg:px-6">
      <div className="max-w-7xl mx-auto space-y-6">
        <Button
          className="hidden lg:flex"
          variant="ghost"
          onClick={() => navigate(-1)}
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        {/* HEADER */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          {/* <div>
            <h1 className="text-3xl font-bold">All Transactions</h1>

            <p className="text-muted-foreground">
              View and manage all platform transactions
            </p>
          </div> */}

          <div className="flex gap-2 w-fit">
            <Button variant="outline" size="icon" onClick={handleRefresh}>
              <RefreshCw
                className={cn("h-4 w-4", isRefreshing && "animate-spin")}
              />
            </Button>

            <Button
              variant="outline"
              onClick={handleExport}
              className="w-full sm:w-auto"
            >
              <Download className="h-4 w-4 mr-2" />
              Export
            </Button>
          </div>
        </div>

        {/* FILTERS */}
        <Card>
          <CardContent className="p-4">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {/* SEARCH */}
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />

                <Input
                  placeholder="Search by ID, user, token or hash..."
                  className="pl-10"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>

              {/* TYPE */}
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Type" />
                </SelectTrigger>

                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="DEPOSIT">Deposit</SelectItem>
                  <SelectItem value="WITHDRAWAL">Withdrawal</SelectItem>
                  <SelectItem value="TRANSFER">Transfer</SelectItem>
                  <SelectItem value="SWAP">Swap</SelectItem>
                  <SelectItem value="MINT">Mint</SelectItem>
                </SelectContent>
              </Select>

              {/* STATUS */}
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>

                <SelectContent>
                  <SelectItem value="ALL">All Status</SelectItem>
                  <SelectItem value="PENDING">Pending</SelectItem>
                  <SelectItem value="PROCESSING">Processing</SelectItem>
                  <SelectItem value="COMPLETED">Completed</SelectItem>
                  <SelectItem value="FAILED">Failed</SelectItem>
                  <SelectItem value="CANCELLED">Cancelled</SelectItem>
                  <SelectItem value="REVERSED">Reversed</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* TABLE */}
        <Card className="border-border/50 shadow-sm">
          <CardHeader>
            <CardTitle>Transactions</CardTitle>
            <CardDescription>Filtered transaction list</CardDescription>
          </CardHeader>

          <CardContent>
            <div className="pb-4">
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">
                  Showing {filteredTransactions.length} transaction
                  {filteredTransactions.length !== 1 && "s"}
                </p>

                <Badge variant="secondary">
                  {meta?.total || filteredTransactions.length} Total
                </Badge>
              </div>
            </div>
            {/* MOBILE VIEW */}
            <div className="block lg:hidden space-y-3 -mx-6">
              {filteredTransactions.map((tx, i) => (
                <div
                  key={tx.id}
                  className="rounded-xl border bg-card p-4 space-y-3"
                >
                  <div className="flex items-center justify-between">
                    <Badge
                      variant="outline"
                      className={cn(
                        "capitalize text-xs",
                        getTypeColor(tx.type),
                      )}
                    >
                      {tx.type}
                    </Badge>

                    <Badge className={getStatusColor(tx.status)}>
                      {tx.status}
                    </Badge>
                  </div>

                  <div>
                    <p className="text-sm font-medium">
                      {tx.tokenSymbol}{" "}
                      {formatEvmHumanBalance(tx?.amount, tx?.isNative)}
                    </p>

                    <p className="text-xs text-muted-foreground mt-1">
                      {tx.tokenName}
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <p className="text-muted-foreground text-xs">Sender</p>

                      <p className="truncate">{tx.user || "-"}</p>
                    </div>

                    <div>
                      <p className="text-muted-foreground text-xs">Receiver</p>

                      <p className="truncate">{tx.receiver || "-"}</p>
                    </div>

                    <div>
                      <p className="text-muted-foreground text-xs">Method</p>

                      <p>{tx.method}</p>
                    </div>

                    <div>
                      <p className="text-muted-foreground text-xs">Time</p>

                      <p>{format(tx.time)}</p>
                    </div>
                  </div>

                  <Button
                    className="w-full"
                    variant="outline"
                    onClick={() => navigate(`/admin/transactions/${tx.id}`)}
                  >
                    <Eye className="h-4 w-4 mr-2" />
                    View Transaction
                  </Button>
                </div>
              ))}

              {!filteredTransactions.length && (
                <div className="text-center py-10 text-muted-foreground">
                  No transactions found
                </div>
              )}
            </div>

            {/* DESKTOP TABLE */}
            <div className="hidden lg:block overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left p-3">#</th>
                    <th className="text-left p-3">Sender</th>
                    <th className="text-left p-3">Receiver</th>
                    <th className="text-left p-3">Type</th>
                    <th className="text-left p-3">Token</th>
                    <th className="text-left p-3">Amount</th>
                    <th className="text-left p-3">Method</th>
                    <th className="text-left p-3">Status</th>
                    <th className="text-left p-3">Time</th>
                    <th className="text-left p-3">Action</th>
                  </tr>
                </thead>

                <tbody>
                  {filteredTransactions.map((tx, i) => (
                    <tr
                      key={tx.id}
                      className="border-b hover:bg-muted/30 transition-colors"
                    >
                      <td className="p-3 text-sm">
                        {((meta?.page || 1) - 1) * limit + i + 1}
                      </td>

                      <td className="p-3 text-sm">{tx.user}</td>

                      <td className="p-3 text-sm">{tx.receiver}</td>

                      <td className="p-3">
                        <Badge
                          variant="outline"
                          className={cn(
                            "capitalize text-xs",
                            getTypeColor(tx.type),
                          )}
                        >
                          {tx.type}
                        </Badge>
                      </td>

                      <td className="p-3 text-sm">{tx?.tokenName}</td>

                      <td className="p-3 text-sm">
                        {tx.tokenSymbol}{" "}
                        {formatEvmHumanBalance(tx?.amount, tx?.isNative)}
                      </td>

                      <td className="p-3 text-sm text-muted-foreground">
                        {tx.method}
                      </td>

                      <td className="p-3">
                        <Badge className={getStatusColor(tx.status)}>
                          {tx.status}
                        </Badge>
                      </td>

                      <td className="p-3 text-sm text-muted-foreground">
                        {format(tx.time)}
                      </td>

                      <td className="p-3">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() =>
                            navigate(`/admin/transactions/${tx.id}`)
                          }
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}

                  {!filteredTransactions.length && (
                    <tr>
                      <td
                        colSpan={10}
                        className="text-center py-10 text-muted-foreground"
                      >
                        No transactions found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* PAGINATION */}
            <div className="flex items-center justify-between gap-4 mt-6 w-full">
              <Button
                variant="outline"
                disabled={!meta?.hasPrevPage}
                onClick={() => setPage((p) => Math.max(p - 1, 1))}
              >
                <ChevronLeft className="h-4 w-4 mr-2" />
                Previous
              </Button>

              <p className="text-xs text-muted-foreground text-center">
                Page {meta?.page || 1} of {meta?.totalPages || 1}
              </p>

              <Button
                variant="outline"
                disabled={!meta?.hasNextPage}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
                <ChevronRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminTransactions;
